import { Injectable } from '@angular/core';
import { PhoneNumberUtil, PhoneNumberFormat, PhoneNumber } from 'google-libphonenumber';
import { countryOption } from 'src/app/shared/utilities/constants';

@Injectable({
  providedIn: 'root'
})
export class PhoneValidatorService {

  private phoneUtil: PhoneNumberUtil;
  private typeMap: { [key: number]: string } = {
    0: 'FIXED_LINE',
    1: 'MOBILE',
    2: 'FIXED_LINE_OR_MOBILE',
    3: 'TOLL_FREE',
    4: 'PREMIUM_RATE',
    5: 'SHARED_COST',
    6: 'VOIP',
    7: 'PERSONAL_NUMBER',
    8: 'PAGER',
    9: 'UAN',
    10: 'VOICEMAIL',
    99: 'UNKNOWN'
  };
  errorMessages = {
    NonSMS : 'Provide a mobile number that can receive texts.',
    InvalidNumber: 'Enter a valid mobile number.',
  }

  constructor() {
    this.phoneUtil = PhoneNumberUtil.getInstance();
  }

  /**
   * Formats phone number using google-libphonenumber
   * Supports international phone numbers with country code detection
   */
  formatPhoneNumber(event: any, phoneControl: any, country: string = 'US'): string {
    const input = event.target as HTMLInputElement;
    const countryCode = countryOption.find(c => c.code === country)?.phoneCode || 'US';

    try {
      const phoneNumber = this.phoneUtil.parse(input.value, countryCode);

      if (this.isValidPhoneNumber(phoneNumber)) {
        // Format as NATIONAL format (e.g., (123) 456-7890)
        const formatted = this.getFormattedNumber(input.value, countryCode, PhoneNumberFormat.INTERNATIONAL);
        input.value = formatted;
        const phoneNumberType = this.getPhoneNumberType(formatted,countryCode);
        if(['MOBILE', 'FIXED_LINE_OR_MOBILE'].includes(phoneNumberType)){
          // Clear custom error on valid input
          if (phoneControl) {
            const errors = phoneControl.errors || {};
            delete errors['custom'];
            phoneControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
          }
        }
        else{
          throw new Error(this.errorMessages.NonSMS);
        }
      } else {
        throw new Error(this.errorMessages.InvalidNumber);
      }
    } catch (error: any) {
      // Set custom error on the form control
      if (phoneControl) {
        const errors = phoneControl.errors || {};
        const errMsg = Object.values(this.errorMessages).find(msg => error.message.includes(msg));
        const customError = errMsg ? errMsg : this.errorMessages.InvalidNumber;
        const htmlString = `<span class="l-form-input-error"><img class="me-2" src="../../../../assets/icons/input-error-icon.png" alt="input-error-icon" />${customError}</span>`;
        errors['custom'] = htmlString;
        phoneControl.setErrors(errors);
      }

      // eslint-disable-next-line no-console
      console.warn('Phone number validation error:', error.message || error);
    }
    const formattedNumber = this.getFormattedNumber(input.value, countryCode, PhoneNumberFormat.INTERNATIONAL);
    return formattedNumber;
  }

  /**
   * Validates phone number
   * Returns true if valid, false otherwise
   */
  isValidPhoneNumber(parsedNumber: PhoneNumber): boolean {
    try {
      return this.phoneUtil.isValidNumber(parsedNumber);
    } catch (error) {
      return false;
    }
  }

  /**
   * Gets the formatted phone number in different formats
   */
  getFormattedNumber(phoneNumber: string, countryCode: string, format: PhoneNumberFormat = PhoneNumberFormat.NATIONAL): string {
    try {
      const parsedNumber = this.phoneUtil.parse(phoneNumber, countryCode);
      if (this.phoneUtil.isValidNumber(parsedNumber)) {
        return this.phoneUtil.format(parsedNumber, format);
      }
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.warn('Phone number formatting error:', error);
    }
    return phoneNumber;
  }

  /**
   * Gets the country code from a phone number
   */
  getCountryCode(phoneNumber: string, regionCode: string): number | null {
    try {
      const parsedNumber = this.phoneUtil.parse(phoneNumber, regionCode);
      return parsedNumber.getCountryCode() || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Gets the phone number type (MOBILE, FIXED_LINE, etc.)
   */
  getPhoneNumberType(phoneNumber: string, countryCode: string): string {
    try {
      const parsedNumber = this.phoneUtil.parse(phoneNumber, countryCode);
      const numberType = this.phoneUtil.getNumberType(parsedNumber);

      return this.typeMap[numberType] || 'UNKNOWN';
    } catch (error) {
      return 'UNKNOWN';
    }
  }

  getCleanPhoneNumber(phoneNumber: string, countryCode: string = 'US'): string {
    try {
      const parsedNumber = this.phoneUtil.parse(phoneNumber, countryCode);
      if (this.phoneUtil.isValidNumber(parsedNumber)) {
        // Returns format like: +12345678901
        return this.phoneUtil.format(parsedNumber, PhoneNumberFormat.E164);
      }
    } catch (error: any) {
      console.warn('Phone number formatting error:', error);
    }
    return phoneNumber;
  }
}
